import React, { useEffect, useMemo, useState } from 'react';
import HeroSection from './HeroSection';
import FeatureShowcase from './FeatureShowcase';
import ManifestoSection from './ManifestoSection';
import EarlyAccessSection from './EarlyAccessSection';
import FloatingUserPill from './FloatingUserPill';

// New modular CSS architecture - base styles only
import './styles/_base.css';

const LandingPage = () => {
  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === 'undefined') return 1440;
    return window.innerWidth;
  });

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDesktop = viewportWidth >= 1025;
  const isTablet = viewportWidth > 768 && viewportWidth < 1025;
  const orbCount = isDesktop ? 12 : isTablet ? 24 : 28;
  const [showDeferredSections, setShowDeferredSections] = useState(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const playParam = (urlParams.get('play') || '').toLowerCase();
      if (playParam.startsWith('demo') || window.location.hash === '#play-demo') {
        return true;
      }
    }
    return false;
  });

  useEffect(() => {
    let idleId;
    let timeoutId;

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(() => {
        setShowDeferredSections(true);
      }, { timeout: 1200 });
    } else {
      timeoutId = window.setTimeout(() => {
        setShowDeferredSections(true);
      }, 450);
    }

    return () => {
      if (idleId && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  // Generate orbs with more variety
  const orbs = useMemo(() => [...Array(orbCount)].map((_, i) => {
    const largeCount = isDesktop ? 4 : 6;
    const mediumCount = isDesktop ? 7 : 8;
    const isLarge = i < largeCount;
    const isMedium = i >= largeCount && i < largeCount + mediumCount;
    
    let size, color, duration;
    
    if (isLarge) {
      size = Math.random() * 20 + 15; // 15-35px
      color = i % 2 === 0 
        ? 'rgba(51, 92, 255, 0.6)' 
        : 'rgba(99, 102, 241, 0.5)';
      duration = isDesktop ? Math.random() * 14 + 35 : Math.random() * 15 + 20;
    } else if (isMedium) {
      size = Math.random() * 10 + 8; // 8-18px
      color = i % 3 === 0 
        ? 'rgba(51, 92, 255, 0.7)' 
        : i % 3 === 1 
          ? 'rgba(99, 102, 241, 0.6)' 
          : 'rgba(139, 92, 246, 0.5)';
      duration = isDesktop ? Math.random() * 12 + 28 : Math.random() * 12 + 15;
    } else {
      size = Math.random() * 5 + 3; // 3-8px small sparkles
      color = i % 4 === 0 
        ? 'rgba(51, 92, 255, 0.9)' 
        : i % 4 === 1 
          ? 'rgba(99, 102, 241, 0.8)' 
          : i % 4 === 2
            ? 'rgba(255, 255, 255, 0.7)'
            : 'rgba(139, 92, 246, 0.7)';
      duration = isDesktop ? Math.random() * 10 + 24 : Math.random() * 10 + 12;
    }
    
    return {
      size,
      x: Math.random() * 100,
      y: Math.random() * 100,
      color,
      duration,
      delay: Math.random() * 8,
      className: isLarge ? 'floating-orb orb-large' : isMedium ? 'floating-orb orb-medium' : 'floating-orb'
    };
  }), [orbCount, isDesktop]);

  return (
    <div className="landing-page">
      <FloatingUserPill />
      {/* Constellation Background */}
      <div className="constellation-bg">
        <div className="constellation-base" />
        
        {/* Floating orbs for ambient effect */}
        {orbs.map((orb, i) => (
          <div
            key={i}
            className={orb.className}
            style={{
              '--orb-size': `${orb.size}px`,
              '--orb-x': `${orb.x}%`,
              '--orb-y': `${orb.y}%`,
              '--orb-color': orb.color,
              '--orb-duration': `${orb.duration}s`,
              '--orb-delay': `${orb.delay}s`,
            }}
          />
        ))}
        
        {/* Glowing accent blobs */}
        <div className="glow-blob blob-1" />
        <div className="glow-blob blob-2" />
        <div className="glow-blob blob-3" />
        
        <div className="mesh-noise" />
      </div>

      {/* Hero Section - Full screen dancer visual */}
      <HeroSection />

      <div className={`deferred-sections ${showDeferredSections ? 'deferred-sections--ready' : ''}`}>
        {/* Feature Showcase - 2 panels */}
        {showDeferredSections ? <FeatureShowcase /> : <section className="deferred-placeholder" aria-hidden="true" />}

        {/* Manifesto Section - Vision & CTA */}
        {showDeferredSections && <ManifestoSection />}

        {/* Early Access Registration Section */}
        {showDeferredSections && <EarlyAccessSection />}
      </div>
    </div>
  );
};

export default LandingPage;
